import React from "react";
import { Badge, Box, Button, Dialog, Flex, IconButton, Separator, Switch, Text, TextArea, TextField } from "@radix-ui/themes";
import { TrashIcon } from "@radix-ui/react-icons";
import { maskWhatsappAccessToken } from "./whatsappCredentialsStore";

function FieldLabel({ children }) {
  return (
    <Text size="2" weight="medium">
      {children}
    </Text>
  );
}

function CredentialField({ label, value, placeholder, type = "text", invalid, errorMessage, onChange }) {
  return (
    <Flex direction="column" gap="1">
      <FieldLabel>{label}</FieldLabel>
      <TextField.Root
        type={type}
        value={value}
        placeholder={placeholder}
        color={invalid ? "red" : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {invalid && errorMessage ? (
        <Text size="1" color="red">
          {errorMessage}
        </Text>
      ) : null}
    </Flex>
  );
}

function AccountSummary({ account, onRemoveAccount }) {
  return (
    <Flex
      justify="between"
      align="center"
      gap="3"
      p="3"
      style={{
        border: "1px solid var(--gray-a6)",
        borderRadius: "var(--radius-4)",
        background: "var(--color-panel-solid)",
        boxShadow: "0 1px 2px var(--gray-a3)",
      }}
    >
      <Flex align="center" gap="3" style={{ minWidth: 0 }}>
        <Flex
          align="center"
          justify="center"
          style={{
            width: 36,
            height: 36,
            flex: "0 0 auto",
            borderRadius: "var(--radius-3)",
            background: "#E9FBF0",
            border: "1px solid #B7EBC8",
          }}
        >
          <img src={require("../../../assets/images/whatsapp.png")} alt="" style={{ width: 20, height: 20 }} />
        </Flex>
        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
          <Flex align="center" gap="2" style={{ minWidth: 0 }}>
            <Text size="2" weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {account.accountName}
            </Text>
            <Badge size="1" color="green" variant="soft">
              Ready
            </Badge>
          </Flex>
          <Text size="1" color="gray" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            App {account.appId} · WhatsApp Business Account {account.businessId} · {account.maskedAccessToken || maskWhatsappAccessToken(account.accessToken)}
          </Text>
        </Flex>
      </Flex>
      <IconButton size="1" variant="ghost" color="red" onClick={() => onRemoveAccount(account.clientKey)}>
        <TrashIcon />
      </IconButton>
    </Flex>
  );
}

export function AuthWhatsappDialog({
  isOpen,
  onOpenChange,
  onCancel,
  onContinue,
  onAddAccount,
  onRemoveAccount,
  accounts,
  formData,
  onFieldChange,
  fieldErrors,
  isAddAccountLoading,
  isContinueLoading,
  isContinueDisabled,
}) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 760, padding: 0, overflow: "hidden" }}>

        {/* Heading */}
        <Flex gap="1" p="5" direction="column">
            <Dialog.Title style={{ margin: 0 }}>Connect WhatsApp Business</Dialog.Title>
            <Dialog.Description size="2" color="gray" mt="1">
              Add the Meta app and WhatsApp Business credentials for template review and messaging.
            </Dialog.Description>
        </Flex>

        {/* Main content */}
        <Flex direction="column" gap="4" p="5">
          <Box style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--space-3)" }}>
            <CredentialField
              label="Account name"
              placeholder="Main WhatsApp account"
              value={formData.accountName}
              invalid={fieldErrors.accountName}
              errorMessage="Account name is required."
              onChange={(value) => onFieldChange("accountName", value)}
            />
            <CredentialField
              label="App ID"
              placeholder="123456789012345"
              value={formData.appId}
              invalid={fieldErrors.appId}
              errorMessage="App ID is required."
              onChange={(value) => onFieldChange("appId", value)}
            />
            <CredentialField
              label="WhatsApp Business Account ID"
              placeholder="987654321098765"
              value={formData.businessId}
              invalid={fieldErrors.businessId}
              errorMessage="WhatsApp Business Account ID is required."
              onChange={(value) => onFieldChange("businessId", value)}
            />
            <CredentialField
              label="Access token"
              type="password"
              placeholder="EAAB..."
              value={formData.accessToken}
              invalid={fieldErrors.accessToken}
              errorMessage="Access token is required."
              onChange={(value) => onFieldChange("accessToken", value)}
            />
          </Box>
          <Flex
            direction="column"
            gap="3"
            p="3"
            style={{
              border: "1px solid var(--gray-a6)",
              borderRadius: "var(--radius-3)",
              background: "var(--gray-1)",
            }}
          >
            <Flex justify="between" align="center" gap="3">
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">
                  Automated inbound reply
                </Text>
                <Text size="1" color="gray">
                  Sent when a guest replies to one of your WhatsApp messages.
                </Text>
              </Flex>
              <Switch
                checked={Boolean(formData.autoReplyEnabled)}
                onCheckedChange={(checked) => onFieldChange("autoReplyEnabled", checked)}
              />
            </Flex>
            <Flex direction="column" gap="1">
              <FieldLabel>Reply message</FieldLabel>
              <TextArea
                value={formData.autoReplyMessage}
                disabled={!formData.autoReplyEnabled}
                onChange={(event) => onFieldChange("autoReplyMessage", event.target.value)}
                style={{ minHeight: 96 }}
              />
            </Flex>
          </Flex>

          <Flex justify="end">
            <Button size="2" variant="soft" onClick={onAddAccount} disabled={isAddAccountLoading}>
              {isAddAccountLoading ? "Adding..." : "Add account"}
            </Button>
          </Flex>

          {accounts.length > 0 ? (
            <Flex direction="column" gap="3">
              <Separator size="4" style={{ minHeight: 1 }} />
              <Flex justify="between" align="center">
                <Text size="2" weight="medium">
                  Accounts to connect
                </Text>
                <Badge color="green" variant="soft">
                  {accounts.length} added
                </Badge>
              </Flex>
              <Flex direction="column" gap="2">
                {accounts.map((account) => (
                  <AccountSummary key={account.clientKey} account={account} onRemoveAccount={onRemoveAccount} />
                ))}
              </Flex>
            </Flex>
          ) : null}
        </Flex>

        {/* Actions */}
        <Flex gap="3" justify="end" p="5" pt="0">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={onCancel}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={onContinue} disabled={isContinueDisabled || isContinueLoading}>
            {isContinueLoading ? "Saving..." : "Save credentials"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
